import React from "react";
import { Link } from "react-router-dom";
import LegalLayout, { LegalSection, LegalList } from "./LegalLayout";

export default function Terms() {
  return (
    <LegalLayout
      title="Terms of Service"
      subtitle="The terms governing your use of nexus-vs and related services provided by Vawick Consulting UG."
      lastUpdated="June 2026"
    >
      <LegalSection title="1. Scope and Provider">
        <p>
          These Terms of Service ("Terms") govern the contractual relationship between
          Vawick Consulting UG (haftungsbeschränkt), [Address], Germany ("Vawick", "we", "us")
          and the organisation accessing or using the nexus-vs software platform
          ("Customer", "you").
        </p>
        <p>
          By registering for or using nexus-vs, you agree to these Terms on behalf of your
          organisation. If you do not agree, do not use the service.
        </p>
        <p>
          nexus-vs is a B2B SaaS product. These Terms apply exclusively to businesses and
          not to consumers.
        </p>
      </LegalSection>

      <LegalSection title="2. Description of Service">
        <p>
          nexus-vs is a contractor operations platform providing tools for onboarding, contract
          management, time tracking, payroll processing, compliance monitoring, and related
          workflow automation. Feature availability depends on your subscription tier.
        </p>
        <p>
          We reserve the right to modify or improve features with reasonable notice. We will
          not remove core functionality during a paid subscription term without providing
          an alternative.
        </p>
      </LegalSection>

      <LegalSection title="3. Account Registration">
        <LegalList items={[
          "You must provide accurate and complete registration information and keep it up to date.",
          "You are responsible for maintaining the confidentiality of your login credentials.",
          "You must promptly notify us of any unauthorised access at support@vawick.de.",
          "Each user account is personal and may not be shared between individuals.",
          "You are responsible for all activity that occurs under your account.",
        ]} />
      </LegalSection>

      <LegalSection title="4. Subscription and Payment">
        <p>
          nexus-vs is offered on a monthly or annual subscription basis. Pricing is set out
          on our{" "}
          <Link to="/partners" className="text-white hover:text-[#FF4F00] transition-colors">
            pricing page
          </Link>{" "}
          and in your order confirmation.
        </p>
        <LegalList items={[
          "Subscriptions are billed in advance on a monthly or annual basis.",
          "All prices are in EUR, exclusive of applicable VAT.",
          "Payment is due within 14 days of invoice date. Late payments may incur statutory default interest.",
          "Annual subscriptions are non-refundable except as required by applicable law.",
          "We reserve the right to adjust pricing with 30 days' written notice before the next renewal period.",
        ]} />
      </LegalSection>

      <LegalSection title="5. Minimum Commitment and Cancellation">
        <p>
          White-label and partner plans require a minimum 3-month commitment as disclosed
          at the time of sign-up.
        </p>
        <LegalList items={[
          "Standard plans may be cancelled with 30 days' written notice to support@vawick.de.",
          "Cancellation takes effect at the end of the current billing period.",
          "On cancellation, you may export your data for 30 days; after which it is securely deleted.",
        ]} />
      </LegalSection>

      <LegalSection title="6. Acceptable Use">
        <p>You agree not to use nexus-vs to:</p>
        <LegalList items={[
          "Process data in violation of applicable laws, including GDPR",
          "Upload malicious code, viruses, or harmful content",
          "Circumvent security controls or attempt unauthorised access to any system",
          "Resell or sublicense the service without our prior written consent (except white-label partners under a signed Partner Agreement)",
          "Use the service for any purpose other than lawful business operations",
          "Reverse engineer, decompile, or attempt to extract the source code of the platform",
        ]} />
      </LegalSection>

      <LegalSection title="7. Intellectual Property">
        <p>
          All intellectual property rights in nexus-vs — including the software, design,
          trademarks, and documentation — remain with Vawick Consulting UG. These Terms grant
          you a limited, non-exclusive, non-transferable licence to use the service during
          your subscription term.
        </p>
        <p>
          You retain ownership of all data you upload to nexus-vs. You grant us a limited
          licence to process your data solely to provide and improve the service.
        </p>
      </LegalSection>

      <LegalSection title="8. Confidentiality">
        <p>
          Each party agrees to keep the other's Confidential Information confidential and not
          to disclose it to third parties without prior written consent. This obligation
          survives termination for 3 years.
        </p>
        <p>
          "Confidential Information" means any non-public information disclosed in connection
          with the service, including pricing, technical architecture, and customer data.
        </p>
      </LegalSection>

      <LegalSection title="9. Limitation of Liability">
        <p>To the maximum extent permitted by applicable law:</p>
        <LegalList items={[
          "Our total liability for any claim arising under these Terms shall not exceed the fees paid by you in the 12 months preceding the claim.",
          "We shall not be liable for indirect, incidental, or consequential damages, loss of profits, or loss of data.",
          "Nothing in these Terms limits liability for fraud, gross negligence, or wilful misconduct.",
          "Nothing limits liability for death or personal injury caused by our negligence.",
        ]} />
      </LegalSection>

      <LegalSection title="10. Data Processing">
        <p>
          To the extent that we process personal data on your behalf as a data processor,
          the terms of our{" "}
          <Link to="/dpa" className="text-white hover:text-[#FF4F00] transition-colors">
            Data Processing Agreement (DPA)
          </Link>{" "}
          apply and are incorporated into these Terms by reference.
        </p>
      </LegalSection>

      <LegalSection title="11. Service Availability">
        <p>
          We target 99.5% monthly uptime for the nexus-vs platform, excluding scheduled
          maintenance windows. We will provide reasonable advance notice of planned downtime.
          Enterprise plans may be eligible for a separate SLA with defined remedies.
        </p>
      </LegalSection>

      <LegalSection title="12. Termination">
        <p>We may suspend or terminate your access immediately if:</p>
        <LegalList items={[
          "You materially breach these Terms and fail to remedy the breach within 14 days of written notice",
          "You fail to pay outstanding fees after 30 days overdue",
          "You engage in fraudulent, abusive, or illegal activity",
        ]} />
        <p>
          Upon termination, your right to access the service ceases. Data export is available
          for 30 days post-termination, after which all data is securely deleted.
        </p>
      </LegalSection>

      <LegalSection title="13. Governing Law and Jurisdiction">
        <p>
          These Terms are governed by German law, excluding the UN Convention on Contracts
          for the International Sale of Goods (CISG).
        </p>
        <p>
          The exclusive place of jurisdiction for all disputes arising from or in connection
          with these Terms is [City], Germany, provided the Customer is a merchant
          (Kaufmann), a legal entity under public law, or a special fund under public law.
        </p>
      </LegalSection>

      <LegalSection title="14. Changes to These Terms">
        <p>
          We may update these Terms with 30 days' written notice. Continued use after the
          effective date constitutes acceptance. If you do not accept the changes, you may
          terminate your subscription before the effective date without penalty.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
